<template>
  <div class="modal-overlay" v-if="visible" @click.self="close">
    <div class="modal-box sltp-modal-box">
      <h3>一键止损止盈</h3>

      <!-- 品种切换 Tab -->
      <div class="sltp-sym-bar">
        <span
          v-for="sym in symbolList"
          :key="sym.key"
          :class="['sltp-sym-tab', { active: selectedSymbol === sym.key }]"
          @click="switchSymbol(sym.key)"
        >{{ sym.label }}</span>
      </div>

      <!-- 当前品种持仓列表（可滚动） -->
      <div class="sltp-scroll-area">
        <div v-if="currentPositions.length === 0" class="sltp-empty-hint">当前品种暂无持仓</div>
        <div v-for="pos in currentPositions" :key="pos.id" class="sltp-pos-item">
          <span :class="['dir-tag', pos.direction === 'buy' ? 'tag-buy' : 'tag-sell']">{{ pos.direction === 'buy' ? '买入' : '卖出' }}</span>
          <span class="sltp-pos-lots">{{ pos.lots }}手</span>
          <span class="sltp-pos-price">开仓 {{ fmtPrice(pos.openPrice, priceDec) }}</span>
          <span class="sltp-pos-price">最新 {{ fmtPrice(pos.currentPrice, priceDec) }}</span>
          <span class="sltp-pos-pnl" :class="pos.pnl >= 0 ? 'up' : 'down'">{{ fmtPnl(pos.pnl) }}</span>
        </div>
      </div>

      <!-- 止损输入 -->
      <div class="sltp-ctrl-row">
        <label class="sltp-ctrl-label">止损</label>
        <div class="sltp-ctrl-group">
          <button class="sltp-ctrl-btn" @click="adjustSLTP('sl', -1)">−</button>
          <input type="number" class="sltp-ctrl-input" v-model="sltpForm.sl" placeholder="未设置" :step="priceStep" />
          <button class="sltp-ctrl-btn" @click="adjustSLTP('sl', 1)">+</button>
        </div>
      </div>

      <!-- 止盈输入 -->
      <div class="sltp-ctrl-row">
        <label class="sltp-ctrl-label">止盈</label>
        <div class="sltp-ctrl-group">
          <button class="sltp-ctrl-btn" @click="adjustSLTP('tp', -1)">−</button>
          <input type="number" class="sltp-ctrl-input" v-model="sltpForm.tp" placeholder="未设置" :step="priceStep" />
          <button class="sltp-ctrl-btn" @click="adjustSLTP('tp', 1)">+</button>
        </div>
      </div>

      <div class="modal-btns">
        <button class="cancel" @click="close">取消</button>
        <button class="confirm" @click="doUpdate">提交</button>
      </div>
    </div>
  </div>
</template>

<script>
import { showToast } from '@/utils/common'

export default {
  name: 'SltpModal',
  props: {
    visible: Boolean,
    position: Object,
    positionCount: { type: Number, default: 0 }
  },
  data() {
    return {
      selectedSymbol: 'XAUUSD',
      sltpForm: { sl: '', tp: '' },
      symbolList: [
        { key: 'XAUUSD', label: '现货黄金' },
        { key: 'XAGUSD', label: '现货白银' }
      ]
    }
  },
  computed: {
    isBatch() {
      return !this.position
    },
    /** 当前选中品种的持仓 */
    currentPositions() {
      const positions = this.$store.state.trade.positions || []
      return positions.filter(p => p.symbol === this.selectedSymbol)
    },
    /** 根据品种确定价格精度 */
    priceDec() {
      return this.selectedSymbol === 'XAUUSD' ? 2 : 3
    },
    priceStep() {
      return this.selectedSymbol === 'XAUUSD' ? '0.01' : '0.001'
    },
    /** 当前市价（取 bid） */
    currentBid() {
      const md = this.$store.state.trade.marketData[this.selectedSymbol]
      return md ? md.bid || md.price : 0
    },
    /** 止损上限 = 当前价 - 200 点 */
    sltpHardMaxSl() {
      const bid = this.currentBid
      if (!bid) return 0
      return this.selectedSymbol === 'XAUUSD' ? bid - 2 : bid - 0.2
    },
    /** 止盈下限 = 当前价 + 200 点 */
    sltpHardMinTp() {
      const bid = this.currentBid
      if (!bid) return 0
      return this.selectedSymbol === 'XAUUSD' ? bid + 2 : bid + 0.2
    }
  },
  methods: {
    close() { this.$emit('close') },
    switchSymbol(sym) {
      this.selectedSymbol = sym
      // 切换品种时，从该品种第一笔持仓中提取已有的 SL/TP
      const firstPos = this.currentPositions[0]
      this.sltpForm = {
        sl: firstPos && firstPos.slPrice != null ? String(firstPos.slPrice) : '',
        tp: firstPos && firstPos.tpPrice != null ? String(firstPos.tpPrice) : ''
      }
    },
    adjustSLTP(field, delta) {
      const val = parseFloat(this.sltpForm[field]) || 0
      const newVal = Math.max(0, val + delta)
      this.$set(this.sltpForm, field, newVal > 0 ? String(newVal) : '')
    },
    async doUpdate() {
      if (!this.sltpForm.sl && !this.sltpForm.tp) return showToast('请至少设置止损或止盈')
      if (this.currentPositions.length === 0) return showToast('当前品种暂无持仓')

      try {
        for (const pos of this.currentPositions) {
          await this.$store.dispatch('trade/updateSltp', {
            orderId: pos.id,
            slPrice: this.sltpForm.sl ? parseFloat(this.sltpForm.sl) : null,
            tpPrice: this.sltpForm.tp ? parseFloat(this.sltpForm.tp) : null
          })
        }
        showToast('止损止盈已设置')
        this.$emit('updated')
        this.close()
      } catch (err) {
        showToast(err.message || '设置失败')
      }
    },
    fmtPrice(val, dec) {
      if (val == null) return '—'
      return val.toFixed(dec)
    },
    fmtPnl(val) {
      if (val == null) return '—'
      const sign = val >= 0 ? '+' : ''
      return sign + val.toFixed(2)
    }
  },
  watch: {
    visible(v) {
      if (v) {
        // 初始化：默认选中第一个有持仓的品种，否则保持当前品种
        const positions = this.$store.state.trade.positions || []
        const firstWithPos = this.symbolList.find(s =>
          positions.some(p => p.symbol === s.key)
        )
        this.selectedSymbol = firstWithPos ? firstWithPos.key : (this.position ? this.position.symbol : 'XAUUSD')

        // 初始化表单
        const firstPos = this.currentPositions[0]
        this.sltpForm = {
          sl: firstPos && firstPos.slPrice != null ? String(firstPos.slPrice) : '',
          tp: firstPos && firstPos.tpPrice != null ? String(firstPos.tpPrice) : ''
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.sltp-modal-box {
  width: 420px;
  max-width: 90vw;
}

.sltp-sym-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.sltp-sym-tab {
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid var(--border);
  background: #fff;
  color: var(--text);
  transition: all 0.2s;
  font-weight: 500;
}
.sltp-sym-tab.active {
  background: #e64340;
  color: #fff;
  border-color: #e64340;
  font-weight: 600;
}

/* 持仓列表 — 可滚动 */
.sltp-scroll-area {
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 8px 10px;
  background: var(--bg);
}

.sltp-empty-hint {
  text-align: center;
  color: var(--text-muted);
  font-size: 13px;
  padding: 12px 0;
}

.sltp-pos-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-size: 13px;
  border-bottom: 1px solid var(--border);
}
.sltp-pos-item:last-child {
  border-bottom: none;
}

.sltp-pos-lots {
  font-weight: 600;
  min-width: 40px;
}
.sltp-pos-price {
  color: var(--text-secondary);
  flex: 1;
}
.sltp-pos-pnl {
  font-weight: 600;
  min-width: 60px;
  text-align: right;
}
.sltp-pos-pnl.up { color: var(--red); }
.sltp-pos-pnl.down { color: var(--green); }

/* 输入控件行 */
.sltp-ctrl-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.sltp-ctrl-label {
  width: 50px;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 500;
}

.sltp-ctrl-group {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
}

.sltp-ctrl-btn {
  width: 36px;
  height: 36px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: #fff;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text);
  transition: background 0.15s;
  line-height: 1;
  padding: 0;
}
.sltp-ctrl-btn:hover {
  background: var(--bg);
}

.sltp-ctrl-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
  color: var(--text);
  text-align: center;
  min-width: 0;
}
.modal-box h3 {
  margin-bottom: 16px;
}
.dir-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: #fff;
  margin-right: 6px;
  font-weight: 500;
}
.tag-buy { background: #e64340; }
.tag-sell { background: #07c160; }
.modal-btns { display: flex; gap: 12px; margin-top: 16px; button { flex: 1; padding: 10px; border-radius: 6px; border: none; cursor: pointer; font-family: inherit; font-weight: 600; } .cancel { background: var(--bg); } .confirm { background: var(--red); color: #fff; } }
</style>
